import { Pipe, PipeTransform } from '@angular/core';
import { Observable, catchError, map, of } from 'rxjs';
import { FileType } from 'src/app/module/file-viewer/interface/file-viewer.interface';
import { PropertyDataSourceType } from 'src/app/module/order/interface/order.interface';
import { PropertyApi } from 'src/app/module/property/api/property.api';
import {
  ExtendedPropertyDetail,
  ImageRecord,
  PropertyAttributeValue,
} from 'src/app/module/property/interface/property.interface';
import { PropertyService } from 'src/app/module/property/service/property.service';
import { PropertyConfig } from '../config/property.config';
import { SelectedSourceDataPipe } from './selected-source-data.pipe';

interface SearchData {
  latitude?: number;
  longitude?: number;
  address?: string;
}

@Pipe({
  name: 'imagePipe',
  standalone: true,
  pure: true,
})
export class ImagePipe implements PipeTransform {
  constructor(
    private propertyApi: PropertyApi,
    private propertyService: PropertyService,
    private selectedSourceDataPipe: SelectedSourceDataPipe
  ) {}

  transform(
    url: any,
    fileType: FileType,
    showStreetView: boolean = false,
    propertyId?: string | null,
    height?: number | null,
    searchData?: SearchData | null
  ): Observable<{ inProgress: boolean; url: string }> {
    return new Observable((observer) => {
      if (!url && propertyId) url = this.getPropertyCoverImageUrl(propertyId);

      let previewUrl = this.getPreviewUrl(fileType, url, height);

      if (previewUrl) {
        observer.next({ inProgress: false, url: previewUrl });
        observer.complete();
      } else if (showStreetView) {
        const data: ExtendedPropertyDetail | null = propertyId
          ? this.propertyService.getPropertyById(propertyId)
          : null;

        for (const source of PropertyConfig.propertyDataSourceList) {
          previewUrl = (data?.imageWithLabels?.[source as keyof PropertyAttributeValue] as ImageRecord[])?.[0]?.url;
          if (previewUrl) break;
        }

        if (!previewUrl) {
          this.getStreetOrSatelliteView(data, searchData ?? null).subscribe((streetOrSatelliteUrl) => {
            observer.next({ inProgress: false, url: streetOrSatelliteUrl ?? '' });
            observer.complete();
          });
        } else {
          observer.next({ inProgress: false, url: previewUrl });
          observer.complete();
        }
      } else {
        observer.next({ inProgress: false, url: '/assets/image-not-found.png' });
        observer.complete();
      }
    });
  }

  getStreetOrSatelliteView(
    data: ExtendedPropertyDetail | null,
    searchData?: SearchData | null
  ): Observable<string | null> {
    const lat = this.selectedSourceDataPipe.transform(data?.Latitude) ?? searchData?.latitude ?? null;
    const lng = this.selectedSourceDataPipe.transform(data?.Longitude) ?? searchData?.longitude ?? null;

    const location = lat && lng ? `${lat},${lng}` : null;

    let address = data
      ? `${this.selectedSourceDataPipe.transform(
          data.propertyStreetAddress
        )},${this.selectedSourceDataPipe.transform(data.propertyCity)},${this.selectedSourceDataPipe.transform(
          data.propertyState
        )},${this.selectedSourceDataPipe.transform(data.propertyZip)}`
      : (searchData?.address ?? null);

    address = address?.replaceAll(' ', '+') ?? null;

    const center = location ?? address;

    if (!center) return of(null);

    return this.propertyApi
      .getStreetOrSatelliteView(
        `https://maps.googleapis.com/maps/api/streetview?key=AIzaSyCDKDlbBP3abIEcnV07uFA6ztj_MxzqLk4&return_error_code=true&`,
        { zoom: 16, size: '600x300', location: center, markers: center }
      )
      .pipe(
        map((res: Blob) => {
          return URL.createObjectURL(res);
        }),

        catchError(() => {
          return this.propertyApi
            .getStreetOrSatelliteView(
              `https://maps.googleapis.com/maps/api/staticmap?key=AIzaSyCDKDlbBP3abIEcnV07uFA6ztj_MxzqLk4&return_error_code=true&maptype=satellite&`,
              { zoom: 19, size: '600x300', center: center, markers: center }
            )
            .pipe(
              map((res: Blob) => {
                return URL.createObjectURL(res);
              }),

              catchError(() => {
                return of(null);
              })
            );
        })
      );
  }

  getPreviewUrl(fileType: FileType, url: string, height?: number | null) {
    let imgSrc = '';
    switch (fileType) {
      case 'image':
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'svg':
        imgSrc = url;
        if (imgSrc?.includes('?') && height) imgSrc = imgSrc + `&height=${height}`;
        break;
      case 'pdf':
        imgSrc = '/assets/pdf-cover.png';
        break;
      case 'csv':
        imgSrc = '/assets/csv-cover.png';
        break;
      default:
        imgSrc = '/assets/image-not-found.png';
        break;
    }
    return imgSrc;
  }

  getPropertyCoverImageUrl(propertyId: string | null | undefined) {
    const data: ExtendedPropertyDetail | null = propertyId ? this.propertyService.getPropertyById(propertyId) : null;
    const sourcePriority: PropertyDataSourceType[] = [
      'userRecord',
      'inspectionRecord',
      'rentalRecord',
      'listingRecord',
      'publicRecord',
    ];

    for (const source of sourcePriority) {
      const frontHouseViewUrl = (data?.imageWithLabels?.[source] as ImageRecord[])?.find(
        (image) => image.label === 'FrontViewHouse'
      )?.url;
      if (frontHouseViewUrl) return frontHouseViewUrl;
    }

    for (const source of sourcePriority) {
      const firstImageUrl = (data?.imageWithLabels?.[source] as ImageRecord[])?.[0]?.url;
      if (firstImageUrl) return firstImageUrl;
    }

    return '';
  }
}
